import { Menu } from 'lucide-react';
import { Suspense, useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import ExpectedLogo from '@/assets/logos/expected-sm.svg?react';
import { Button } from '@/components/ui/Button';
import { PageLoader } from '@/components/ui/PageLoader';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { UserIntentWidget } from '../widgets/UserIntentWidget';
import { Footer } from './Footer';
import { JoinRequestBanner } from './JoinRequestBanner';
import { Sidebar } from './Sidebar';

const ROUTES_WITHOUT_FOOTER = ['/faxes/send'];

export function AuthenticatedLayout({
  children,
}: {
  children?: React.ReactNode;
}) {
  const location = useLocation();
  const showFooter = !ROUTES_WITHOUT_FOOTER.includes(location.pathname);
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleCloseSidebar = useCallback(
    () => setIsMobileSidebarOpen(false),
    []
  );
  const handleOpenSidebar = useCallback(() => setIsMobileSidebarOpen(true), []);

  return (
    <TooltipProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-2 focus:bg-primary focus:text-primary-foreground"
      >
        Skip to main content
      </a>
      <div className="flex min-h-screen">
        {isMobile ? (
          <Sidebar
            isMobile
            isOpen={isMobileSidebarOpen}
            onClose={handleCloseSidebar}
          />
        ) : (
          <Sidebar />
        )}
        <div className="flex-1 flex flex-col min-w-0">
          {isMobile && (
            <header className="sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b bg-background">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenSidebar}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <ExpectedLogo />
              <div className="w-9" />
            </header>
          )}
          <main id="main-content" className="flex-1 bg-background-subtle">
            <JoinRequestBanner />
            {/* Nested Suspense so lazy route chunks only swap the content area —
                the sidebar/header stay mounted instead of the whole shell being
                replaced by the top-level App Suspense fallback. h-full keeps the
                spinner centered within <main> (which is flex-1 of a min-h-screen
                column) instead of overflowing it like the default h-screen. */}
            <Suspense fallback={<PageLoader className="h-full" />}>
              {children || <Outlet />}
            </Suspense>
          </main>
          {showFooter && <Footer className="bg-background-subtle" />}
        </div>
      </div>
      <UserIntentWidget />
    </TooltipProvider>
  );
}
